/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2019 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import InjectableComponent from '../Utility/Component/ComponentReference/InjectableComponent';
import NodeReferenceData from '../Utility/Component/Node/NodeReferenceData';
import ObservableDoubleComponent from '../Utility/Component/Observable/ObservableDoubleComponent';
import ObservableIntComponent from '../Utility/Component/Observable/ObservableIntComponent';
import CLog, { LogTypes } from '../Utility/ComponentLogger';
import Effect from './Effect';
import NodeCondition from './NodeCondition';

const {ccclass, property} = cc._decorator;

@ccclass
export default class ConditionalShowNodeEffect extends Effect
{
    @property()
    protected show: boolean = true;

    @property(InjectableComponent)
    protected compareToRef: InjectableComponent<ObservableDoubleComponent> = new InjectableComponent();

    @property()
    protected isDouble: boolean = false;

    @property([NodeCondition])
    protected conditions: NodeCondition[] = [new NodeCondition()];

    private _defaultCondition: NodeCondition = null;

    private _foundObservable: ObservableDoubleComponent = null;

    private _componentType: { new(): cc.Component } = null;

    protected setContexts(): void
    {
        super.setContexts();

        this._componentType = this.isDouble ? ObservableDoubleComponent : ObservableIntComponent;

        this.compareToRef.SetContext(this._componentType);
    }

    protected onLoad(): void {
        super.onLoad();

        this._defaultCondition = this.conditions.pop();
    }

    protected start(): void {
        super.start();

        this.compareToRef.WithValue(compareTo =>
            {
                this._foundObservable = compareTo.getComponent(this._componentType);
            },
            () => CLog.error('Observable node not found.', this, LogTypes.Initialization));
    }

    /** Executes the effect. */
    protected trigger = (): void =>
    {
        this.reset();

        const found: cc.Node = this.evaulateNode();
        if (!found) { return; }

        found.active = this.show;
        this.complete();
    }

    private evaulateNode(): cc.Node
    {
        if (!this._defaultCondition) { return null; }
        if (this.conditions.length < 1
            || !this._foundObservable
            || !this._foundObservable.HasValue()) { return this._defaultCondition.NodeReference.Value; }

        for (let condition of this.conditions)
        {
            if (condition.Condition === this._foundObservable.Value)
            {
                return condition.NodeReference.Value;
            }
        }

        return this._defaultCondition.NodeReference.Value;
    }

    private reset(): void {
        this.conditions.forEach(condition => {
            this.setActive(condition.NodeReference, !this.show);
        }, this);

        this.setActive(this._defaultCondition.NodeReference, !this.show);
    }

    private setActive(nodeRef: NodeReferenceData, to: boolean): void {
        if (!nodeRef.HasValue()) { return; }
        nodeRef.Value.active = to;
    }
}
